import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Navigation, Loader2 } from "lucide-react";

// Ensure cookies are sent with requests
axios.defaults.withCredentials = true;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;

// Custom marker icons by company + scheduled status
const createIcon = (color, size = 28, isMain = false) => {
  const s = isMain ? size + 8 : size;
  const border = isMain ? "3px solid #1F2937" : "2px solid white";
  const shadow = isMain ? "0 0 0 3px rgba(0,0,0,0.2), 0 2px 8px rgba(0,0,0,0.3)" : "0 1px 4px rgba(0,0,0,0.3)";
  return L.divIcon({
    className: "",
    iconSize: [s, s],
    iconAnchor: [s / 2, s / 2],
    popupAnchor: [0, -s / 2],
    html: `<div style="width:${s}px;height:${s}px;border-radius:50%;background:${color};border:${border};box-shadow:${shadow};display:flex;align-items:center;justify-content:center;">
      ${isMain ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>' : ''}
    </div>`,
  });
};

const ICONS = {
  main: createIcon("#1F2937", 28, true),
  safeguard_scheduled: createIcon("#2563EB", 22),
  safeguard_unscheduled: createIcon("#93C5FD", 20),
  national_scheduled: createIcon("#DC2626", 22),
  national_unscheduled: createIcon("#FCA5A5", 20),
};

const getIcon = (order) => {
  const company = (order.company || "").toLowerCase();
  const scheduled = order.scheduled;
  if (company.includes("safeguard")) return scheduled ? ICONS.safeguard_scheduled : ICONS.safeguard_unscheduled;
  if (company.includes("national")) return scheduled ? ICONS.national_scheduled : ICONS.national_unscheduled;
  return ICONS.safeguard_unscheduled;
};

// Auto-fit map bounds
const FitBounds = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [map, positions]);
  return null;
};

const OrderMapSection = ({ orderId }) => {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMapData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/orders/${orderId}/map-data`);
      if (res.data.error) {
        setError(res.data.error);
      } else {
        setMapData(res.data);
      }
    } catch (err) {
      setError("Failed to load map data");
    }
    setLoading(false);
  }, [orderId]);

  useEffect(() => { fetchMapData(); }, [fetchMapData]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="order-map-loading">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="h-4 w-4 text-[#2F5D8A]" />
          <h3 className="text-sm font-bold text-[#1F2937] uppercase tracking-wider">Location Map</h3>
        </div>
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-5 w-5 text-[#2F5D8A] animate-spin" />
          <span className="ml-2 text-sm text-gray-400">Geocoding address...</span>
        </div>
      </div>
    );
  }

  if (error || !mapData?.current_order) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="order-map-error">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="h-4 w-4 text-[#2F5D8A]" />
          <h3 className="text-sm font-bold text-[#1F2937] uppercase tracking-wider">Location Map</h3>
        </div>
        <div className="text-center py-8 text-gray-400 text-sm">
          <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-200" />
          <p>{error || "Address could not be located"}</p>
          <button onClick={fetchMapData} className="mt-2 text-xs text-[#2F5D8A] hover:underline">Retry</button>
        </div>
      </div>
    );
  }

  const { current_order, nearby_orders, total_nearby } = mapData;
  const center = [current_order.lat, current_order.lng];
  const allPositions = [center, ...nearby_orders.map(o => [o.lat, o.lng])];

  // Count by type
  const safScheduled = nearby_orders.filter(o => o.company?.includes("Safeguard") && o.scheduled).length;
  const safUnscheduled = nearby_orders.filter(o => o.company?.includes("Safeguard") && !o.scheduled).length;
  const natScheduled = nearby_orders.filter(o => o.company?.includes("National") && o.scheduled).length;
  const natUnscheduled = nearby_orders.filter(o => o.company?.includes("National") && !o.scheduled).length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="order-map-section">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[#2F5D8A]" />
          <h3 className="text-sm font-bold text-[#1F2937] uppercase tracking-wider">Location Map</h3>
          <span className="text-xs text-gray-400 ml-1">
            {total_nearby} nearby order{total_nearby !== 1 ? "s" : ""} within 15 mi
          </span>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-3 text-[10px]">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#2563EB] border border-white shadow-sm inline-block" /> SG Sched</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#93C5FD] border border-white shadow-sm inline-block" /> SG Unsched</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#DC2626] border border-white shadow-sm inline-block" /> NAT Sched</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#FCA5A5] border border-white shadow-sm inline-block" /> NAT Unsched</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 rounded-full bg-[#1F2937] border-2 border-white shadow-sm inline-block" /> Current</span>
        </div>
      </div>

      {/* Map */}
      <div style={{ height: "400px" }} data-testid="map-container">
        <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap'
          />
          <FitBounds positions={allPositions} />

          {/* 15-mile radius circle */}
          <Circle center={center} radius={24140} pathOptions={{ color: "#2F5D8A", fillColor: "#2F5D8A", fillOpacity: 0.03, weight: 1, dashArray: "6 4" }} />

          {/* Nearby order pins */}
          {nearby_orders.map((o) => (
            <Marker key={o.id} position={[o.lat, o.lng]} icon={getIcon(o)}>
              <Popup maxWidth={280}>
                <div className="text-xs space-y-1 min-w-[200px]">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-[#1F2937]">#{o.order_number}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                      o.company?.includes("Safeguard") ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"
                    }`}>{o.company}</span>
                  </div>
                  <p className="text-gray-600">{o.address}</p>
                  <p className="text-gray-500">{o.city} &middot; {o.status}</p>
                  {o.appointment_real_time && (
                    <p className="text-[#4A7A56] font-medium">Window: {o.appointment_real_time}</p>
                  )}
                  {o.assigned_inspector && (
                    <p className="text-[#2F5D8A]">Inspector: {o.assigned_inspector}</p>
                  )}
                  <p className="text-gray-400">{o.distance_miles} mi away</p>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Current order pin (on top) */}
          <Marker position={center} icon={ICONS.main} zIndexOffset={1000}>
            <Popup maxWidth={280}>
              <div className="text-xs space-y-1 min-w-[200px]">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#1F2937] text-sm">#{current_order.order_number}</span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-800 text-white">Current Order</span>
                </div>
                <p className="text-gray-600">{current_order.address}</p>
                <p className="text-gray-500">{current_order.city}, {current_order.state}</p>
                <p className="text-gray-500">Status: {current_order.status}</p>
                {current_order.appointment_real_time && (
                  <p className="text-[#4A7A56] font-medium">Window: {current_order.appointment_real_time}</p>
                )}
                {current_order.assigned_inspector && (
                  <p className="text-[#2F5D8A]">Inspector: {current_order.assigned_inspector}</p>
                )}
              </div>
            </Popup>
          </Marker>
        </MapContainer>
      </div>

      {/* Summary bar */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center gap-4 text-[10px]" data-testid="map-summary">
        <span className="text-gray-400 font-medium uppercase tracking-wider">Nearby:</span>
        <span className="text-blue-700 font-medium">{safScheduled} SG Scheduled</span>
        <span className="text-blue-400 font-medium">{safUnscheduled} SG Unscheduled</span>
        <span className="text-red-600 font-medium">{natScheduled} NAT Scheduled</span>
        <span className="text-red-400 font-medium">{natUnscheduled} NAT Unscheduled</span>
        <span className="ml-auto text-gray-400 flex items-center gap-1"><Navigation className="h-3 w-3" /> 15 mi radius</span>
      </div>
    </div>
  );
};

export default OrderMapSection;
